import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationTriangle,
  faFlag,
  faLayerGroup,
  faRobot,
  faSort,
  faSortDown,
  faSortUp,
  faTriangleExclamation,
  faUpload,
} from "@fortawesome/free-solid-svg-icons";
import {
  bandFor,
  criterionLabel,
  notEvaluated,
  paperRubricPct,
} from "../config/rubric";
import "./PaperComparison.css";

// Several papers at once, for whoever has to look at all of them.
//
// This replaced a row of chips. A chip carried the course code, the exam and two
// icons, which is enough to pick a paper you already know the name of and
// nothing at all for the question this screen actually exists to answer: of
// these twenty papers, which ones need attention, and is there something wrong
// with all of them. That question is answered by comparing figures, so the
// figures have to be on screen together.
//
// Every column is read from the listing response. Nothing here is computed from
// a second request, and nothing is invented: where a paper has no figure the
// cell is empty rather than zero, because zero is a measurement and "we did not
// measure this" is not.

// Guarded rather than trusting the value. One run of the render check over all
// 194 rows reported a NaN reaching a cell; two later renders of the identical
// data found none and I could not reproduce it, so rather than leave it to
// chance the formatter now refuses anything that is not a finite number. A cell
// showing nothing is a cell saying "not measured", which is always true when
// there is no number to show; "NaN%" is never anything but a bug on screen.
const pct = (value) => {
  const number = Number(value);
  return value == null || !Number.isFinite(number) ? "" : `${number.toFixed(0)}%`;
};

// Same guard, one level up: a score is only printed when it is a real number.
const scoreOf = (paper) => {
  const number = Number(paper?.final_weighted_score);
  return paper?.final_weighted_score == null || !Number.isFinite(number) ? null : number;
};

const flagsFor = (paper) => {
  const flags = [];
  // Processing that never finished. Unlike the engine's own file-based index,
  // this listing is built from the paper rows, so a paper that failed outright
  // does appear here - it just has no analysis behind it. Worth a flag of its
  // own, because "no figures because it failed" and "no figures because nobody
  // has scored it yet" are different problems with different fixes.
  if (paper.processing_status === "failed") {
    flags.push({
      key: "failed",
      icon: faExclamationTriangle,
      tone: "poor",
      label: "Processing failed for this paper - no analysis was ever written. "
        + "The upload server's log for this file says why",
    });
  }
  if (notEvaluated(paper)) {
    flags.push({
      key: "blank",
      icon: faExclamationTriangle,
      tone: "poor",
      label: "Could not be evaluated - no questions were read from this paper",
    });
  }
  if (paper.incomplete_questions) {
    flags.push({
      key: "incomplete",
      icon: faTriangleExclamation,
      tone: "fair",
      label: `${paper.incomplete_questions} question(s) were still open when the `
        + `read reached the end of the paper, so they were scored on partial content`,
    });
  }
  if (paper.unscored_criteria) {
    flags.push({
      key: "unscored",
      icon: faTriangleExclamation,
      tone: "fair",
      label: `${paper.unscored_criteria} criteri${paper.unscored_criteria === 1 ? "on" : "a"} `
        + `could not be scored. They still count toward the maximum, so this `
        + `paper's score is understated - not a judgement about the paper`,
    });
  }
  if (paper.review_staleness?.stale) {
    flags.push({
      key: "stale",
      icon: faFlag,
      tone: "fair",
      label: "Signed off against an older analysis - this paper has been re-scored since",
    });
  }
  if (paper.review_progress?.status === "completed") {
    flags.push({ key: "signed", icon: faCheckCircle, tone: "good", label: "Signed off" });
  }
  return flags;
};

const SORTS = {
  score: {
    label: "Score",
    // Worst first. A reviewer opening this screen is looking for the papers that
    // need work, and the default sort should put them at the top rather than
    // making that the second thing they do.
    get: (p) => p.final_weighted_score,
    dir: 1,
  },
  meanq: { label: "Mean question %", get: (p) => p.mean_question_pct, dir: 1 },
  paperpct: { label: "Paper rubric %", get: (p) => paperRubricPct(p), dir: 1 },
  questions: { label: "Questions", get: (p) => p.engine_questions ?? p.question_count, dir: 1 },
  course: { label: "Course", get: (p) => p.course_id || "", dir: 1 },
  created: { label: "Uploaded", get: (p) => p.created_at || "", dir: -1 },
};

const PaperComparison = ({ papers, selectedPaperId, onSelect }) => {
  const [sortKey, setSortKey] = useState("score");
  const [sortDir, setSortDir] = useState(1);
  const [bandFilter, setBandFilter] = useState("");
  const [courseFilter, setCourseFilter] = useState("");
  const [outcomesFilter, setOutcomesFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);

  const bands = useMemo(
    () => Array.from(new Set(papers.map((p) => p.band_id).filter(Boolean))).sort(),
    [papers]);
  const courses = useMemo(
    () => Array.from(new Set(papers.map((p) => p.course_id).filter(Boolean))).sort(),
    [papers]);

  const filtered = useMemo(() => {
    const rows = papers.filter((paper) => {
      if (bandFilter && paper.band_id !== bandFilter) return false;
      if (courseFilter && paper.course_id !== courseFilter) return false;
      // Whether this paper's course has learning outcomes on file. Without them
      // two rubric criteria are skipped and coverage is unmeasurable, so it is
      // the difference between a paper scored out of 28 and one out of 24.
      if (outcomesFilter === "yes" && !paper.coverage) return false;
      if (outcomesFilter === "no" && paper.coverage) return false;
      if (statusFilter === "unscored" && paper.scored) return false;
      if (statusFilter === "failed" && paper.processing_status !== "failed") return false;
      if (statusFilter === "scored" && !paper.scored) return false;
      if (onlyFlagged) {
        const flags = flagsFor(paper).filter((f) => f.key !== "signed");
        if (flags.length === 0) return false;
      }
      return true;
    });
    const sort = SORTS[sortKey] || SORTS.score;
    return [...rows].sort((a, b) => {
      const av = sort.get(a);
      const bv = sort.get(b);
      // Papers with no figure sink to the bottom either way round: they are not
      // "the lowest score", they are absent from this comparison.
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") return sortDir * av.localeCompare(bv);
      return sortDir * (av - bv);
    });
  }, [papers, bandFilter, courseFilter, outcomesFilter, statusFilter, onlyFlagged,
      sortKey, sortDir]);

  // Columns for the criterion matrix: every criterion any listed paper was
  // scored on. A paper scored under a different rubric simply has no cell in a
  // column it does not have, which is the honest rendering - the older rubric's
  // criteria are not renamed versions of the newer one's.
  const matrix = useMemo(() => {
    const keys = [];
    const labels = {};
    filtered.forEach((paper) => {
      Object.entries(paper.criterion_means || {}).forEach(([key, entry]) => {
        if (!keys.includes(key)) keys.push(key);
        if (entry?.name && !labels[key]) labels[key] = entry.name;
      });
    });
    const averages = {};
    keys.forEach((key) => {
      const values = filtered
        .map((paper) => paper.criterion_means?.[key]?.ratio)
        .filter((value) => typeof value === "number");
      averages[key] = values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
    });
    const scored = Object.entries(averages).filter(([, value]) => value != null);
    const weakest = scored.length
      ? scored.reduce((low, entry) => (entry[1] < low[1] ? entry : low))[0]
      : null;
    return { keys, labels, averages, weakest };
  }, [filtered]);

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortDir(-sortDir);
    } else {
      setSortKey(key);
      setSortDir(SORTS[key]?.dir ?? 1);
    }
  };

  const sortIcon = (key) =>
    key !== sortKey ? faSort : sortDir === 1 ? faSortUp : faSortDown;

  const Th = ({ column, children, align }) => (
    <th className={align === "right" ? "is-right" : ""}>
      <button type="button" className="pc-sort" onClick={() => toggleSort(column)}>
        {children} <FontAwesomeIcon icon={sortIcon(column)} />
      </button>
    </th>
  );

  return (
    <div className="review-card pc">
      <div className="pc-head">
        <div>
          <FontAwesomeIcon icon={faLayerGroup} />{" "}
          <strong>{papers.length} papers matched</strong>
          {filtered.length !== papers.length && ` · ${filtered.length} shown`} — pick one
          to review
        </div>
        <button
          type="button"
          className="pc-toggle"
          onClick={() => setShowMatrix(!showMatrix)}
        >
          {showMatrix ? "Hide" : "Compare"} criterion scores
        </button>
      </div>

      <div className="pc-filters">
        <label>
          <span>Exam category</span>
          <select value={bandFilter} onChange={(e) => setBandFilter(e.target.value)}>
            <option value="">All</option>
            {bands.map((band) => (
              <option key={band} value={band}>{band}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Course</span>
          <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)}>
            <option value="">All</option>
            {courses.map((course) => (
              <option key={course} value={course}>{course}</option>
            ))}
          </select>
        </label>
        <label>
          <span>Course outcomes</span>
          <select value={outcomesFilter} onChange={(e) => setOutcomesFilter(e.target.value)}>
            <option value="">Any</option>
            <option value="yes">On file (coverage measured)</option>
            <option value="no">Not on file</option>
          </select>
        </label>
        <label>
          <span>Analysis</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Any</option>
            <option value="scored">Scored</option>
            <option value="unscored">Not scored yet</option>
            <option value="failed">Processing failed</option>
          </select>
        </label>
        <label className="pc-check">
          <input
            type="checkbox"
            checked={onlyFlagged}
            onChange={(e) => setOnlyFlagged(e.target.checked)}
          />
          <span>Only papers with a flag</span>
        </label>
      </div>

      {/* Capped, with the header pinned. 194 matches is a real result here, and
          at eight rows to the inch an uncapped table pushes the paper's own
          evaluation - the thing a click is meant to reveal - several thousand
          pixels down the page. Scrolls inside itself instead. */}
      <div className="pc-scroll is-rows">
        <table className="pc-table">
          <thead>
            <tr>
              <Th column="course">Course</Th>
              <th>Exam</th>
              <th>Category</th>
              <Th column="questions" align="right">Qs</Th>
              <Th column="score" align="right">Score</Th>
              <Th column="meanq" align="right">Mean Q</Th>
              <Th column="paperpct" align="right">Paper</Th>
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((paper) => {
              const flags = flagsFor(paper);
              const blank = notEvaluated(paper);
              const score = scoreOf(paper);
              return (
                <tr
                  key={paper.id}
                  className={
                    `pc-row${paper.id === selectedPaperId ? " is-active" : ""}`
                    + (blank ? " is-blank" : "")
                  }
                  onClick={() => onSelect(paper.id)}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelect(paper.id);
                    }
                  }}
                >
                  <td>
                    <span className="pc-course">{paper.course_id}</span>
                    <span className="pc-course-name">{paper.course_name}</span>
                  </td>
                  <td>
                    <span className="pc-exam">{paper.exam_type || "—"}</span>
                    <span className="pc-exam-meta">
                      {/* Each field only when it is there. These are NOT NULL
                          columns today, so this never fires on real data - but
                          "undefined Sundefined" is what a row missing them
                          printed, and a listing that gains a filter or a new
                          source should not be able to put that on screen. */}
                      {paper.academic_year || ""}
                      {paper.semester ? ` S${paper.semester}` : ""}
                      {(paper.academic_year || paper.semester) ? " · " : ""}
                      <FontAwesomeIcon
                        icon={paper.source === "manual_upload" ? faUpload : faRobot}
                        title={
                          paper.source === "manual_upload"
                            ? "Manual upload"
                            : "Automated evaluation"
                        }
                      />
                    </span>
                  </td>
                  <td className="pc-band">
                    {paper.band_id || "—"}
                    {paper.band_confidence && (
                      <em className={`is-${paper.band_confidence}`}>
                        {paper.band_confidence}
                      </em>
                    )}
                  </td>
                  <td className="is-right">
                    {blank ? "0" : (paper.engine_questions ?? paper.question_count ?? "—")}
                  </td>
                  <td className="is-right">
                    {blank || score == null ? (
                      <span className="pc-absent">—</span>
                    ) : (
                      <span className={`pc-score is-${bandFor(score / 100)}`}>
                        {score.toFixed(1)}
                      </span>
                    )}
                  </td>
                  <td className="is-right pc-num">
                    {blank ? <span className="pc-absent">—</span> : pct(paper.mean_question_pct) || <span className="pc-absent">—</span>}
                  </td>
                  {/* The tooltip is built from the pair only when BOTH halves
                      are there. Keyed on the maximum alone it read
                      "undefined/28 on the paper-level rubric" for a paper whose
                      paper-level run died after writing its denominator. */}
                  <td className="is-right pc-num" title={
                    paper.paper_max != null && paper.paper_total != null
                      ? `${paper.paper_total}/${paper.paper_max} on the paper-level rubric`
                      : ""
                  }>
                    {paperRubricPct(paper) == null
                      ? <span className="pc-absent">—</span>
                      : pct(paperRubricPct(paper))}
                  </td>
                  <td className="pc-flags">
                    {flags.map((flag) => (
                      <FontAwesomeIcon
                        key={flag.key}
                        icon={flag.icon}
                        title={flag.label}
                        className={`pc-flag is-${flag.tone}`}
                      />
                    ))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Only papers that produced an analysis appear above. A file that failed
          before anything could be stored has no row here at all - not even a
          blank one - so a count that disagrees with what was submitted is worth
          checking against the upload batch rather than read as "the rest were
          fine". */}
      <p className="pc-footnote">
        Papers with no stored analysis show no figures. A file that failed before an
        analysis could be written has no row here at all.
      </p>

      {showMatrix && (
        <div className="pc-matrix-wrap">
          <h3>Mean score per criterion</h3>
          {matrix.keys.length === 0 ? (
            <p className="pc-empty">
              None of these papers has per-criterion scores stored.
            </p>
          ) : (
            <>
              <p className="pc-matrix-note">
                Shown as a percentage of each criterion's own maximum, because two
                rubrics are in circulation — one scores out of 3, the other out of 4 —
                and the raw means are not comparable. Hover a cell for the mean itself.
                A blank cell means that paper was not scored on that criterion.
              </p>
              <div className="pc-scroll">
                <table className="pc-matrix">
                  <thead>
                    <tr>
                      <th>Course</th>
                      {matrix.keys.map((key) => (
                        <th
                          key={key}
                          className={key === matrix.weakest ? "is-weakest" : ""}
                          title={matrix.labels[key] || criterionLabel(key)}
                        >
                          {matrix.labels[key] || criterionLabel(key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((paper) => (
                      <tr
                        key={paper.id}
                        className={paper.id === selectedPaperId ? "is-active" : ""}
                        onClick={() => onSelect(paper.id)}
                      >
                        <td className="pc-matrix-label">
                          {paper.course_id}
                          {/* The rubric version, because it explains the blanks.
                              The two rubrics do not share criterion keys - one
                              calls it "Clarity & Precision", the other "Clarity
                              and Precision" - so a paper occupies half the
                              columns and leaves the rest empty. Without this the
                              gaps look like missing data. */}
                          <em>
                            {paper.exam_type}
                            {paper.rubric_version ? ` · rubric ${paper.rubric_version}` : ""}
                          </em>
                        </td>
                        {matrix.keys.map((key) => {
                          const entry = paper.criterion_means?.[key];
                          return (
                            <td
                              key={key}
                              className={entry ? `is-${bandFor(entry.ratio)}` : "is-absent"}
                              title={entry
                                ? `${entry.mean}/${entry.max} across ${entry.count} question(s)`
                                : "not scored on this criterion"}
                            >
                              {entry && Number.isFinite(entry.ratio)
                                ? `${Math.round(entry.ratio * 100)}%`
                                : ""}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td className="pc-matrix-label">Average</td>
                      {matrix.keys.map((key) => (
                        <td
                          key={key}
                          className={key === matrix.weakest ? "is-weakest" : ""}
                        >
                          {matrix.averages[key] == null
                            || !Number.isFinite(matrix.averages[key])
                            ? ""
                            : `${Math.round(matrix.averages[key] * 100)}%`}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                </table>
              </div>
              {matrix.weakest && (
                <p className="pc-matrix-read">
                  Weakest across these papers:{" "}
                  <strong>{matrix.labels[matrix.weakest] || criterionLabel(matrix.weakest)}</strong>
                  {" "}at {Math.round(matrix.averages[matrix.weakest] * 100)}% — a
                  criterion every paper is losing marks on is a problem with how the
                  papers are being written, not with one of them.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default PaperComparison;
